import { OffsetPaginationEvent } from '@crczp/utils';
import {
    AbstractLevelBasic,
    AccessLevel,
    AssessmentLevel,
    HintBasic,
    InfoLevel,
    Level,
    TrainingDefinition,
    TrainingDefinitionBasic,
    TrainingDefinitionInfo,
    TrainingDefinitionStateEnum,
    TrainingDefinitionWithLevels,
    TrainingLevel
} from '@crczp/training-model';
import { Observable } from 'rxjs';
import { OffsetPaginatedResource, QueryParam } from '@crczp/api-common';
import { TrainingDefinitionSort } from '../sorts';

export abstract class LinearTrainingDefinitionApi {
    /**
     * Sends http request to retrieve all training definitions on specified page of a pagination
     * @param pagination requested pagination
     * @param filters filters to be applied on result
     */
    abstract getAll(
        pagination: OffsetPaginationEvent<TrainingDefinitionSort>,
        filters?: QueryParam[],
    ): Observable<OffsetPaginatedResource<TrainingDefinition>>;

    /**
     * Sends http request to retrieve all training instances on specified page of a pagination for organizer (different access rights)
     * @param pagination requested pagination
     * @param filters filters to be applied on result
     */
    abstract getAllForOrganizer(
        pagination: OffsetPaginationEvent<TrainingDefinitionSort>,
        filters?: QueryParam[],
    ): Observable<OffsetPaginatedResource<TrainingDefinitionInfo>>;

    /**
     * Sends http request to retrieve training definition by its id
     * @param id id of training definition
     */
    abstract get(id: number): Observable<TrainingDefinitionWithLevels>;

    /**
     * Sends http request to change state of a training definition
     * @param trainingDefinitionId id of a training definition which state should be changed
     * @param newState new state to be set
     */
    abstract changeState(
        trainingDefinitionId: number,
        newState: TrainingDefinitionStateEnum,
    ): Observable<any>;

    /**
     * Sends http request to retrieve level by id
     * @param levelId id of level which should be retrieved
     */
    abstract getLevel(levelId: number): Observable<Level>;

    /**
     * Sends request to download training definition json file.
     * @param id id of training definition which should be downloaded
     */
    abstract download(id: number): Observable<boolean>;

    /**
     * Sends http request to upload training definition json file,
     * Converts training definition file to a JSON object and sends it to provided url.
     * @param file json file to be uploaded
     */
    abstract upload(file: File): Observable<TrainingDefinitionWithLevels>;

    /**
     * Sends http request to delete training definition
     * @param id id of training definition which should be deleted
     */
    abstract delete(id: number): Observable<any>;

    /**
     * Sends http request to clone training definition
     * @param id id of training definition which should be cloned.
     * @param title title of new cloned training definition
     */
    abstract clone(id: number, title: string): Observable<number>;

    /**
     * Sends http request to update training definition
     * @param trainingDefinition training definition to update
     */
    abstract update(
        trainingDefinition: TrainingDefinitionWithLevels,
    ): Observable<number>;

    /**
     * Sends http request to create new training definition
     * @param trainingDefinition training definition which should be created
     */
    abstract create(
        trainingDefinition: TrainingDefinitionWithLevels,
    ): Observable<TrainingDefinitionWithLevels>;

    /**
     * Sends http request to create new assessment level associated with training definition
     * @param trainingDefinitionId id of training definition which should be associated with the new level
     */
    abstract createAssessmentLevel(
        trainingDefinitionId: number,
    ): Observable<AssessmentLevel>;

    /**
     * Sends http request to create new training level associated with training definition
     * @param trainingDefinitionId id of training definition which should be associated with the new level
     */
    abstract createTrainingLevel(
        trainingDefinitionId: number,
    ): Observable<TrainingLevel>;

    /**
     * Sends http request to create new access level associated with training definition
     * @param trainingDefinitionId id of training definition which should be associated with the new level
     */
    abstract createAccessLevel(
        trainingDefinitionId: number,
    ): Observable<AccessLevel>;

    /**
     * Sends http request to create new info level associated with training definition
     * @param trainingDefId id of training definition which should be associated with the new level
     */
    abstract createInfoLevel(trainingDefId: number): Observable<InfoLevel>;

    /**
     * Sends http request to delete level
     * @param trainingDefinitionId id of training definition associated with the level which should be deleted
     * @param levelId id of level which should be deleted
     */
    abstract deleteLevel(
        trainingDefinitionId: number,
        levelId: number,
    ): Observable<Level[]>;

    /**
     * Sends http request to update training definition levels
     * @param trainingDefinitionId id of training definition associated with the levels
     * @param levels training levels which should be updated
     */
    abstract updateTrainingDefinitionLevels(
        trainingDefinitionId: number,
        levels: Level[],
    ): Observable<any>;

    /**
     * Sends http request to move level to new position (change order)
     * @param trainingDefinitionId id of training definition associated with the level
     * @param levelId id of a level which should be moved
     * @param toPosition index of new position of a level
     */
    abstract moveLevelTo(
        trainingDefinitionId: number,
        levelId: number,
        toPosition: number,
    ): Observable<Level[]>;

    abstract fetchLevelsByIds(ids: number[]): Observable<AbstractLevelBasic[]>;

    abstract fetchTrainingDefinitionsByIds(
        ids: number[],
    ): Observable<TrainingDefinitionBasic[]>;

    abstract fetchHintsByIds(ids: number[]): Observable<HintBasic[]>;
}
